import { User } from '../types';
import { logInfo, logWarn } from '../utils/logger';

/**
 * RBAC helper for current backend permission strings.
 */
export class RBACService {
  private user: User | null = null;

  constructor(user: User | null = null) {
    this.user = user;
  }

  /**
   * Update the current user for permission checks
   */
  setUser(user: User | null): void {
    this.user = user;
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: string): boolean {
    if (!this.user) {
      logWarn('RBAC', 'No user set for permission check');
      return false;
    }

    if (this.user.role === 'super_admin') {
      return true;
    }

    return Array.isArray(this.user.permissions) && this.user.permissions.includes(permission);
  }

  /**
   * Get Smart Insights access entries for the current user.
   */
  getAccessibleSmartInsightsEntries(): BotAccess[] {
    if (!this.user) return [];

    const entries: BotAccess[] = [];

    // Smart Insights
    if (this.hasPermission('smart_insights_bot.access')) {
      entries.push({
        id: 'finance-v2',
        name: 'SmartInsights',
        path: '/smart-insights',
        department: 'finance'
      });
    }

    logInfo('RBAC', `User ${this.user.email} (role: ${this.user.role}, dept: ${this.user.department}) has access to ${entries.length} Smart Insights entries: ${entries.map(entry => entry.name).join(', ')}`);
    return entries;
  }

  /**
   * Get accessible menu items for sidebar
   */
  getAccessibleMenuItems(): MenuItem[] {
    if (!this.user) return [];

    const items: MenuItem[] = [];

    // Add Smart Insights when permitted.
    const smartInsightsEntries = this.getAccessibleSmartInsightsEntries();
    smartInsightsEntries.forEach(bot => {
      items.push({
        id: bot.id,
        label: bot.name,
        path: bot.path,
        category: 'bots'
      });
    });

    // Notifications - accessible to all
    items.push({
      id: 'notifications',
      label: 'Notifications',
      path: '/notifications',
      category: 'core'
    });

    // Admin-only items
    if (this.user.role === 'super_admin') {
      items.push({
        id: 'users',
        label: 'Users',
        path: '/users',
        category: 'admin'
      });
    }

    return items;
  }

  /**
   * Check if user can access a specific route
   */
  canAccessRoute(path: string): boolean {
    if (!this.user) return false;

    // Admin routes
    if (path === '/users') {
      return this.user.role === 'super_admin';
    }

    // Bot routes
    const botRoutes = {
      '/smart-insights': 'smart_insights_bot.access',
    };

    const requiredPermission = botRoutes[path as keyof typeof botRoutes];
    if (requiredPermission) {
      return this.hasPermission(requiredPermission);
    }

    // Default allow for other routes
    return true;
  }

  /**
   * Get user role display information
   */
  getUserRoleInfo(): UserRoleInfo {
    if (!this.user) {
      return { role: 'Unknown', department: 'Unknown', accessLevel: 'none' };
    }

    return {
      role: this.user.role,
      department: this.user.department || 'Not Assigned',
      accessLevel: this.user.role === 'super_admin' ? 'full' : 'limited'
    };
  }
}

/**
 * Types for RBAC service
 */
export interface BotAccess {
  id: string;
  name: string;
  path: string;
  department: string;
}

export interface MenuItem {
  id: string;
  label: string;
  path: string;
  category: 'core' | 'bots' | 'management' | 'admin';
}

export interface UserRoleInfo {
  role: string;
  department: string;
  accessLevel: 'full' | 'limited' | 'none';
}

// Singleton instance
let rbacInstance: RBACService | null = null;

/**
 * Get or create RBAC service instance
 */
export const getRBACService = (user: User | null = null): RBACService => {
  if (!rbacInstance || user !== rbacInstance['user']) {
    rbacInstance = new RBACService(user);
  }
  return rbacInstance;
};

export default RBACService;
